import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { FolderManagerItemTypes } from '../../../interfaces';
import { ChevronDownIcon, PinIcon, PlusIcon, SearchIcon } from '../../../components/UI/NgIcons';
import { CircularLoader } from '../../../shared';

export type SearchFilterOption = { value: string; label: string };

interface HomeSearchBarProps {
  placeholder?: string;
  items?: HomeSearchItem[];
  actions?: HomeSearchAction[];
  onSearch?: (query: string) => void;
  onFilterChange?: (value: string) => void;
  searchButton?: boolean;
  filterOptions?: SearchFilterOption[];
  typeOrder?: FolderManagerItemTypes[];
  typeLabels?: Partial<Record<FolderManagerItemTypes, string>>;
  isLoading?: boolean;
}

const Wrapper = styled.div`
  position: relative;
  margin: auto;
  width: 100%;
  max-width: ${({ theme }) => theme.layout.pageMaxWidth};
  margin-left: 45px;
`;

const Container = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: stretch;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.white};
  border: 1.5px solid ${({ $open, theme }) => ($open ? theme.palette.textLinkColor : theme.palette.cardBorderDarker)};
  border-radius: ${({ $open, theme }) => ($open ? `${theme.radius.card} ${theme.radius.card} 0 0` : theme.radius.card)};
  box-shadow: ${({ $open, theme }) => ($open ? `0 0 0 3px rgba(37,99,235,0.10)` : theme.shadows.sm)};
  transition: border-color 0.2s, box-shadow 0.2s, border-radius 0.15s;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: none;
  color: ${({ theme }) => theme.palette.textStrong};
  background: ${({ theme }) => theme.palette.cardSurface};
  border-right: 1.5px solid ${({ theme }) => theme.palette.cardBorderDarker};
  font-size: ${({ theme }) => theme.fontSize.button};
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  align-self: stretch;
  flex-shrink: 0;
  min-width: 130px;
  justify-content: space-between;

  &:hover {
    background: ${({ theme }) => theme.palette.cardSurface};
  }

  svg {
    width: 12px;
    height: 12px;
    color: ${({ theme }) => theme.palette.textMuted};
    flex-shrink: 0;
  }
`;

const FilterMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 900;
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.cardBorder};
  border-radius: ${({ theme }) => theme.radius.card};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  min-width: 140px;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FilterMenuItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  font-size: ${({ theme }) => theme.fontSize.button};
  font-family: inherit;
  color: ${({ $active, theme }) => ($active ? theme.palette.textLinkColor : theme.palette.textStrong)};
  background: ${({ $active, theme }) => ($active ? theme.palette.linkBackgroundHover : 'transparent')};
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};

  &:hover {
    background: ${({ theme }) => theme.palette.linkBackgroundHover};
    color: ${({ theme }) => theme.palette.textLinkColor};
  }
`;

const InputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 12px;
  color: ${({ theme }) => theme.palette.textMuted};
  pointer-events: none;
  display: flex;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Input = styled.input`
  cursor: pointer;
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.input};
  box-sizing: border-box;
  padding: 12px 80px 12px 34px;
  color: ${({ theme }) => theme.palette.textStrong};
  font-family: ${({ theme }) => theme.fontFamily.main};

  &::placeholder {
    color: ${({ theme }) => theme.palette.grayLighter};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`;

const SearchButton = styled.button`
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 20px;
  background: ${({ theme }) => theme.palette.primaryButton};
  color: ${({ theme }) => theme.palette.white};
  border: none;
  font-size: ${({ theme }) => theme.fontSize.button};
  font-weight: 700;
  font-family: inherit;
  transition: background 0.15s;
  align-self: stretch;

  &:hover {
    background: ${({ theme }) => theme.palette.primaryButtonHover};
  }

  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
`;

const SearchButtonLoader = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: inherit;
  border-radius: inherit;
`;

const SpinRing = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: search-btn-spin 0.7s linear infinite;

  @keyframes search-btn-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ShortcutBadge = styled.div<{ $hidden: boolean }>`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  align-items: center;
  gap: 4px;
  pointer-events: none;
`;

const KeyCap = styled.span`
  background: ${({ theme }) => theme.palette.cardSurface};
  border: 1px solid ${({ theme }) => theme.palette.cardBorder};
  border-radius: ${({ theme }) => theme.radius.xSmall};
  padding: 2px 6px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.textMuted};
  font-family: inherit;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 800;
  background: ${({ theme }) => theme.palette.white};
  border: 1.5px solid ${({ theme }) => theme.palette.textLinkColor};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme.radius.card} ${({ theme }) => theme.radius.card};
  box-shadow: ${({ theme }) => theme.shadows.xl}, 0 0 0 3px rgba(37, 99, 235, 0.1);
  max-height: 440px;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.cardBorder};
    border-radius: 99px;
  }
`;

const GroupLabel = styled.div`
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.07em;
  padding: 10px 18px 4px;
  font-size: ${({ theme }) => theme.fontSize.badge};
  color: ${({ theme }) => theme.palette.textMuted};
`;

const ResultRow = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 18px;
  transition: background 0.1s;
  background: ${({ $selected, theme }) => ($selected ? theme.palette.linkBackgroundHover : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.palette.linkBackgroundHover};
  }

  &:hover .sd-badge,
  &.selected .sd-badge {
    background: ${({ theme }) => theme.palette.linkBackground};
    color: ${({ theme }) => theme.palette.textLinkColor};
  }
`;

const RowColorBar = styled.div<{ $type: string }>`
  width: 3px;
  align-self: stretch;
  border-radius: 99px;
  flex-shrink: 0;
  background: ${({ $type, theme }) => theme.palette.itemTypeColors[$type] ?? '#64748b'};
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.body};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textStrong};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  mark {
    background: transparent;
    color: ${({ theme }) => theme.palette.textLinkColor};
    font-weight: 700;
  }
`;

const RowSubtext = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.textMuted};
`;

const TypeBadge = styled.span<{ $type: string }>`
  font-size: ${({ theme }) => theme.fontSize.badge};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.white};
  background: ${({ $type, theme }) => theme.palette.itemTypeColors[$type] ?? theme.palette.textMuted};
  border-radius: ${({ theme }) => theme.radius.xSmall};
  padding: 2px 7px;
  flex-shrink: 0;
`;

const PinButton = styled.button<{ $pinned?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px 5px;
  color: ${({ $pinned, theme }) => ($pinned ? theme.palette.textLinkColor : theme.palette.cardBorderHover)};
  border-radius: ${({ theme }) => theme.radius.xSmall};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: all 0.15s;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    color: ${({ theme }) => theme.palette.textLinkColor};
    background: ${({ theme }) => theme.palette.linkBackground};
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 18px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${({ theme }) => theme.palette.linkBackgroundHover};
  }
`;

const ActionIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: ${({ theme }) => theme.palette.linkBackground};
  color: ${({ theme }) => theme.palette.textLinkColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionName = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.body};
  color: ${({ theme }) => theme.palette.textLinkColor};
`;

const Divider = styled.div`
  height: 1px;
  margin: 4px 0;
  background: ${({ theme }) => theme.palette.cardSurface};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 18px;
  border-top: 1px solid ${({ theme }) => theme.palette.cardSurface};
  background: ${({ theme }) => theme.palette.cardBackground};
  border-radius: 0 0 ${({ theme }) => theme.radius.card} ${({ theme }) => theme.radius.card};
`;

const FooterHint = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.textMuted};
`;

const EmptyMessage = styled.div`
  padding: 28px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.body};
  color: ${({ theme }) => theme.palette.textMuted};

  strong {
    color: ${({ theme }) => theme.palette.textMedium};
  }
`;

const highlight = (text: string, query: string): React.ReactNode => {
  if (!query) {
    return text;
  }

  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) {
    return text;
  }

  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent);

export const HomeSearchBar: FC<HomeSearchBarProps> = ({
  placeholder: customPlaceholder,
  items = [],
  actions = [],
  onSearch,
  onFilterChange,
  searchButton = false,
  filterOptions = [],
  typeOrder = [],
  typeLabels = {},
  isLoading = false,
}) => {
  const filterNames = filterOptions
    .filter((option) => option.value)
    .slice(0, 4)
    .map((option) => option.label.toLowerCase());

  const computedPlaceholder =
    filterNames.length === 0
      ? 'Search…'
      : filterNames.length === 1
      ? `Search ${filterNames[0]}…`
      : `Search ${filterNames.slice(0, -1).join(', ')} and ${filterNames.at(-1)}…`;

  const placeholder = customPlaceholder ?? computedPlaceholder;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [filterType, setFilterType] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  let rowIdx = 0;

  const typeFilteredItems = filterType ? items.filter((item) => item.type === filterType) : items;

  const filteredItems = typeFilteredItems;

  const groupedItems = typeOrder.reduce<Record<string, HomeSearchItem[]>>((acc, type) => {
    const group = filteredItems.filter((item) => item.type === type);
    if (group.length) acc[type] = group;
    return acc;
  }, {});

  const flatRows = query.trim() ? typeOrder.flatMap((type) => groupedItems[type] ?? []) : filteredItems;

  const close = useCallback(() => {
    setOpen(false);
    setFilterOpen(false);
    setQuery('');
    setSelectedIdx(0);
    onSearch?.('');
    inputRef.current?.blur();
  }, [onSearch]);

  const collapseDropdown = useCallback(() => {
    setOpen(false);
    setFilterOpen(false);
    setSelectedIdx(0);
    inputRef.current?.blur();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, flatRows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      const selected = flatRows[selectedIdx];
      if (selected) {
        selected.onSelect?.(selected);
        collapseDropdown();
      }
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const handleChange = (value: string) => {
    setQuery(value);
    setSelectedIdx(0);
    onSearch?.(value);
  };

  // ⌘K / Ctrl+K global shortcut
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        setSelectedIdx(0);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // click outside to close
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  return (
    <Wrapper ref={wrapRef}>
      <Container $open={open}>
        {searchButton && (
          <FilterButton type="button" onClick={() => setFilterOpen((prev) => !prev)}>
            {filterOptions.find((option) => option.value === filterType)?.label ?? filterOptions[0].label}

            <ChevronDownIcon />
          </FilterButton>
        )}

        <InputWrap>
          <SearchIconWrap>
            <SearchIcon />
          </SearchIconWrap>

          <Input
            ref={inputRef}
            type="text"
            value={query}
            placeholder={placeholder}
            autoComplete="off"
            onFocus={() => {
              setOpen(true);
              setSelectedIdx(0);
            }}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <ShortcutBadge $hidden={open}>
            <KeyCap>{isMac ? '⌘' : 'Ctrl'}</KeyCap>
            <KeyCap>K</KeyCap>
          </ShortcutBadge>
        </InputWrap>

        {searchButton && (
          <SearchButton
            type="button"
            disabled={isLoading}
            onClick={() => {
              inputRef.current?.focus();
              setOpen(true);
            }}
          >
            <SearchIcon />
            Search
            {isLoading && (
              <SearchButtonLoader>
                <SpinRing />
              </SearchButtonLoader>
            )}
          </SearchButton>
        )}
      </Container>

      {filterOpen && (
        <FilterMenu>
          {filterOptions.map((opt) => (
            <FilterMenuItem
              key={opt.value}
              type="button"
              $active={filterType === opt.value}
              onClick={() => {
                setFilterType(opt.value);
                onFilterChange?.(opt.value);
                setFilterOpen(false);
                setSelectedIdx(0);
              }}
            >
              {opt.label}
            </FilterMenuItem>
          ))}
        </FilterMenu>
      )}

      {open && (
        <Dropdown>
          <ScrollArea>
            {actions.length > 0 && (
              <>
                <GroupLabel>Quick Actions</GroupLabel>
                {actions.map((action, i) => (
                  <ActionRow
                    key={i}
                    onClick={() => {
                      if (action.isLoading) return;
                      action.onSelect?.(action);
                    }}
                  >
                    <ActionIcon>{action.isLoading ? <CircularLoader size={14} /> : <PlusIcon />}</ActionIcon>
                    <ActionName>{action.label}</ActionName>
                  </ActionRow>
                ))}
                {query.trim() && <Divider />}
              </>
            )}

            {query.trim() && (
              isLoading ? (
                <EmptyMessage><CircularLoader size={20} /></EmptyMessage>
              ) : (
                typeOrder.map((type) => {
                  const group = groupedItems[type];
                  if (!group) return null;
                  return (
                    <div key={type}>
                      <GroupLabel>{typeLabels[type] ?? type}s</GroupLabel>
                      {group.map((item) => {
                        const currentIdx = rowIdx++;
                        return (
                          <ResultRow
                            key={item.id ?? item.name}
                            $selected={currentIdx === selectedIdx}
                            onClick={() => {
                              item.onSelect?.(item);
                              collapseDropdown();
                            }}
                          >
                            <RowColorBar $type={item.type} />
                            <RowText>
                              <RowName>{highlight(item.name, query)}</RowName>
                              <RowSubtext>{item.sub}</RowSubtext>
                            </RowText>
                            <TypeBadge $type={item.type}>{typeLabels[item.type] ?? item.type}</TypeBadge>
                            {item.onPin && (
                              <PinButton
                                $pinned={item.isPinned}
                                title={item.isPinned ? 'Unpin' : 'Pin'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  item.onPin?.(item);
                                }}
                              >
                                <PinIcon filled={item.isPinned} />
                              </PinButton>
                            )}
                          </ResultRow>
                        );
                      })}
                    </div>
                  );
                })
              )
            )}
          </ScrollArea>

          <Footer>
            <FooterHint>
              <KeyCap>↑</KeyCap>
              <KeyCap>↓</KeyCap> navigate
            </FooterHint>
            <FooterHint>
              <KeyCap>↵</KeyCap> open
            </FooterHint>
            <FooterHint>
              <KeyCap>esc</KeyCap> close
            </FooterHint>
          </Footer>
        </Dropdown>
      )}
    </Wrapper>
  );
};
